/**
 * Messages Sentry drops before `beforeSend` ever runs.
 *
 * Kept here rather than inline in `sentry.client.config.js` so tests can assert
 * that a message we deliberately instrument does not collide with an entry.
 * Sentry matches these by SUBSTRING, not equality — `isMatchingPattern` in
 * @sentry/utils does `value.includes(pattern)` for string patterns — so
 * 'Failed to fetch' silently swallows anything containing that phrase,
 * including a message as specific as 'Failed to fetch shipping rates'. GB #1527
 * lost exactly that: the non-JSON-body fallback (502/504/HTML error page) was
 * the case where the new vendor context IS the whole diagnosis, and it was the
 * one case that never reached Sentry.
 *
 * Adding an entry here therefore mutes every current and future message that
 * merely CONTAINS it. Before adding one, grep for the phrase in src/.
 */
export const SENTRY_IGNORE_ERRORS: string[] = [
  // Network errors from blocked tracking pixels
  'NetworkError when attempting to fetch resource',
  'Failed to fetch',
  'Load failed',
  // Common third-party script errors
  'fb_xd_fragment',
  'Script error.',
  // Firebase App Check rejects with string 'cancelled' on navigation/unmount
  // This is a Firebase SDK issue - they reject with a string instead of Error
  'Non-Error promise rejection captured with value: cancelled',
];

/**
 * True when `message` would be dropped by the list above. Exported for tests
 * that pin an instrumented error message against accidental muting.
 */
export const isIgnoredBySentry = (message: string): boolean =>
  SENTRY_IGNORE_ERRORS.some((pattern) => message.includes(pattern));
